import { AuthController } from "@somewear/auth";
import type { IFirebaseAuthService } from "@somewear/firebase";
import { firebaseCredentialFromJson } from "@somewear/firebase";
import type { OptionalError } from "@somewear/model";
import { appActions } from "@web/app/appActions";
import type { IPendingSignIn } from "@web/app/appSlice";
import type { RootState } from "@web/app/rootReducer";
import type { IControlledInput } from "@web/common/ControlledInputController";
import ControlledInputController from "@web/common/ControlledInputController";
import EmailInputController from "@web/common/EmailInputController";
import Loader from "@web/common/Loader";
import Paths from "@web/common/Paths";
import { modifyState } from "@web/common/utils";
import * as React from "react";
import { connect } from "react-redux";
import type { RouteComponentProps } from "react-router";
import { Route, Switch } from "react-router";
import type { Dispatch } from "redux";
import { of } from "rxjs";
import { catchError, mergeMap } from "rxjs/operators";

import Config from "../config/Config";
import LoginView from "./LoginView";
import PasswordResetView from "./PasswordResetView";
import PhoneController from "./PhoneController";

const AuthErrors = {
	WrongPassword: "auth/wrong-password",
	UserNotFound: "auth/user-not-found",
	InvalidEmail: "auth/invalid-email",
} as const;

interface IState extends OptionalError, IProps {
	loading: boolean;
}

interface IProps {
	pendingSignIn?: IPendingSignIn;
}

interface IDispatchProps {
	signIn: (username: string, password: string) => void;
}

type CombinedProps = RouteComponentProps & IDispatchProps;

class LoginController extends React.Component<CombinedProps, IState> {
	emailInput: IControlledInput;
	passwordInput: IControlledInput;
	useFirebase: boolean;

	constructor(props: CombinedProps) {
		super(props);

		this.useFirebase = Config.firebase.enable;

		this.state = {
			error: undefined,
			loading: false,
		};

		this.mailLogin = this.mailLogin.bind(this);
		this.resetPassword = this.resetPassword.bind(this);
		this.showReset = this.showReset.bind(this);

		if (this.useFirebase) {
			this.emailInput = new EmailInputController({
				enterAction: this.mailLogin,
			});
		} else {
			this.emailInput = new ControlledInputController({
				enterAction: this.mailLogin,
			});
		}

		this.passwordInput = new ControlledInputController({
			enterAction: this.mailLogin,
		});
	}

	mailLogin(evt?: React.FormEvent<HTMLButtonElement>) {
		evt && evt.preventDefault();

		const email = this.emailInput.getValue();
		const password = this.passwordInput.getValue();

		this.setState(modifyState(this.state, { loading: true }));

		if (!this.useFirebase) {
			AuthController.service
				?.signInWithEmailAndPassword$({
					email: email,
					password: password,
				})
				.pipe(this.getErrorHandler())
				.subscribe(
					() => {
						console.log("sign in successful");
					},
					(error) => {
						console.error("sign in error");
					},
				);
		} else {
			AuthController.service
				?.signInWithEmailAndPassword$({
					email: email,
					password: password,
				})
				.pipe(
					mergeMap((result) => {
						if (this.state.pendingSignIn !== undefined) {
							return (
								AuthController.service as IFirebaseAuthService
							).linkWithCredential(
								firebaseCredentialFromJson(
									this.state.pendingSignIn.pendingCredentialJson,
								),
							);
						}
						return of();
					}),
					this.getErrorHandler(),
				)
				.subscribe(
					() => {
						console.log("successfully linked accounts");
					},
					(error) => {
						console.log("failed to link accounts");
					},
				);
		}
	}

	showReset() {
		this.setState(modifyState(this.state, { error: undefined }));
		this.props.history.push(Paths.Reset);
	}

	resetPassword() {
		this.setState(modifyState(this.state, { loading: true }));
		const email = this.emailInput.getValue();
		AuthController.service.sendPasswordResetEmail$(email).subscribe(
			() => {
				console.log("reset email sent");
				this.setState(
					modifyState(this.state, {
						loading: false,
						error: { message: "Password reset email sent" },
					}),
				);
			},
			(error) => {
				console.log("couldn't send reset email");
				this.setState(
					modifyState(this.state, {
						loading: false,
						error: { message: "ERROR: Could not send reset email" },
					}),
				);
			},
		);
	}

	render(): React.ReactNode {
		if (this.state.loading) {
			return <Loader fullScreen={true} />;
		} else
			return (
				<Switch>
					<Route path={Paths.Reset}>
						<PasswordResetView
							emailInput={this.emailInput}
							resetPassword={this.resetPassword}
							error={this.state.error}
						/>
					</Route>
					<Route path={Paths.Email}>
						<LoginView
							{...this.state}
							emailInput={this.emailInput}
							passwordInput={this.passwordInput}
							mailLogin={this.mailLogin}
							showReset={this.showReset}
							useFirebase={this.useFirebase}
						/>
					</Route>
					<Route path={Paths.SignInPhone}>
						<PhoneController />
					</Route>
				</Switch>
			);
	}

	getErrorHandler() {
		return catchError((error) => {
			if (Object.values(AuthErrors).includes(error.code)) {
				error.message =
					"Email and/or password is incorrect. Please try again or reset your password.";
			} else {
				console.log(error);
			}
			this.setState(modifyState(this.state, { error: error, loading: false }));
			console.log("failed to log in with email");
			return of();
		});
	}
}

function mapStateToProps(state: RootState): IProps {
	return { pendingSignIn: state.app.pendingSignIn };
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
	signIn: (username: string, password: string) =>
		dispatch(
			appActions.signIn.request({
				data: {
					username: username,
					password: password,
				},
			}),
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginController);
